/**
 * STAGE-1 PLACEHOLDER ART — deliberately outside the doc 16 registry.
 *
 * The Core Dome, the wall, and the D1 creatures ship here as honest placeholder
 * sprites so the skeleton is playable ("if rectangles aren't fun, sprites won't
 * save us" — doc 13 §F). They follow the SPIRIT of the art laws (anchor-up
 * volumes, single dark outline, shape language: player rectilinear / fauna
 * curved, cold enemy light) but are NOT harness-checked and carry no goldens by
 * name. Each graduates into `src/art/entities/` with a proper spec sheet and
 * passes A–F in its own build-order stage; nothing in here may be copied there.
 */

import { isoCylinder, isoDiamondRim, isoDisc, isoPrism, applyOutline, newEntityCanvas, toSprite, type Sprite } from '../render/primitives.ts';
import { rampFor, sigRamp, SIGNATURE_ECONOMY } from './materials.ts';
import { TH, TW } from '../render/iso.ts';
import { groundRamp, OUTLINE, ruinRamp, type DepthPalette } from '../render/palette.ts';
import { hex, mix, PixelSurface, shade, TRANSPARENT } from '../render/surface.ts';

// ---------------------------------------------------------------------------
// structures
// ---------------------------------------------------------------------------

export interface PlaceholderRender {
  ground: Sprite;
  body: Sprite;
}

/** The Core Dome, 3×3 ("The Landlord"). Tall by doc 16's own exemption. */
export function drawCoreDome(depth: DepthPalette, damaged: boolean): PlaceholderRender {
  const n = 3;
  const c = newEntityCanvas(n, 46, 10);
  const r = rampFor(depth);
  const sig = sigRamp(SIGNATURE_ECONOMY);

  // plinth and stepped dome — rectilinear player geometry
  isoPrism(c, { n, inset: 4, height: 5, ramp: r });
  let lift = 5;
  for (const [rx, h] of [
    [30, 8],
    [24, 8],
    [17, 7],
    [10, 6],
  ] as const) {
    isoCylinder(c, { rx, height: h, lift, ramp: r });
    lift += h;
  }
  // the lantern: the colony's one warm beacon
  const lampY = c.ay - lift - 2;
  c.s.fillRect(c.ax - 2, lampY - 3, 4, 4, damaged ? sig.dim : sig.hot, 'set');
  c.s.px(c.ax - 3, lampY - 2, sig.halo);
  c.s.px(c.ax + 2, lampY - 2, sig.halo);
  // porthole band on the widest drum
  for (let k = -2; k <= 2; k++) {
    c.s.fillRect(c.ax + k * 7 - 1, c.ay - 9, 2, 2, damaged ? sig.dim : sig.on, 'set');
  }
  applyOutline(c.s);

  return { ground: placeholderGround(depth, n), body: toSprite(c, n) };
}

/** Neighbour-connection bits for walls, in GRID axes. */
export const WALL_SE = 1; // +i
export const WALL_SW = 2; // +j
export const WALL_NW = 4; // -i
export const WALL_NE = 8; // -j

/**
 * Kelp-Crete wall, 1×1, auto-tiling (stakeholder-approved, doc 13 §J).
 * `mask` marks which neighbours are walls: a half-step rampart arm is drawn
 * toward each. My SE arm and the neighbour's NW arm occupy the same world
 * pixels, so a row of sprites reads as ONE continuous rampart — and because
 * the southern sprite draws later (painter order), its body overwrites the
 * northern sprite's outline along the seam. Sixteen variants, all cached.
 */
export function drawWall(depth: DepthPalette, ruined: boolean, mask = 0): PlaceholderRender {
  const n = 1;
  const c = newEntityCanvas(n, 14, 8);
  const ramp = ruined ? ruinRamp(depth.structureBase) : rampFor(depth);
  if (ruined) {
    // rubble never connects — the rampart is visibly broken at this block
    isoPrism(c, { n, inset: 6, height: 2, ramp });
    c.s.fillRect(c.ax - 4, c.ay - 6, 2, 3, ramp.left, 'set');
    c.s.fillRect(c.ax + 2, c.ay - 5, 2, 2, ramp.right, 'set');
    applyOutline(c.s);
    return { ground: placeholderGround(depth, n), body: toSprite(c, n) };
  }

  // rampart rhythm: tall posts, lower curtain arms — reads as ONE wall with
  // crenellation, not a row of identical teeth
  const POST_H = 9;
  const ARM_H = 6;
  const arm = (dx: number, dy: number): void => {
    isoPrism(c, { n, inset: 5, height: ARM_H, dx, dy, ramp });
  };
  // back to front: the north-side arms, the post, then the south-side arms
  if (mask & WALL_NE) arm(8, -4);
  if (mask & WALL_NW) arm(-8, -4);
  isoPrism(c, { n, inset: 5, height: POST_H, ramp });
  if (mask & WALL_SW) arm(-8, 4);
  if (mask & WALL_SE) arm(8, 4);
  if (mask === 0) c.s.px(c.ax, c.ay - POST_H - 2, ramp.edge); // lone post keeps its rivet
  applyOutline(c.s);
  // The outline must run ALONG the rampart, never across it: dilation puts a
  // 1 px stamp past each arm tip, which would land on the neighbour's segment
  // (and theirs on ours). Erase our outline beyond the seam plane on every
  // connected side — both sprites do it, so the joint is seamless.
  const eraseOutlineBeyond = (fromX: number, toX: number): void => {
    for (let y = 0; y < c.s.height; y++) {
      for (let x = fromX; x <= toX; x++) {
        if (c.s.get(x, y) === OUTLINE) c.s.px(x, y, TRANSPARENT, 'set');
      }
    }
  };
  if (mask & (WALL_SE | WALL_NE)) eraseOutlineBeyond(c.ax + 13, c.s.width - 1);
  if (mask & (WALL_NW | WALL_SW)) eraseOutlineBeyond(0, c.ax - 14);
  return { ground: placeholderGround(depth, n), body: toSprite(c, n) };
}

/** Render-side state for placeholder economy buildings. */
export type PlaceholderState = 'working' | 'stalled' | 'offgrid' | 'ruin';

/**
 * The stage-2 economy buildings, placeholder pass. Distinct massing per doc 10
 * rule 6 (no two share a silhouette family), gold signature light that reads
 * the working/stalled/off-grid state, everything outlined. Real pipeline
 * passes come with their content stage.
 */
export function drawEconomyBuilding(
  def: string,
  depth: DepthPalette,
  state: PlaceholderState,
): PlaceholderRender {
  const sizes: Record<string, number> = {
    smelter: 2,
    bullet_kitchen: 2,
    gas_tap: 2,
    refinery: 2,
    generator: 2,
    storage_depot: 2,
    power_pylon: 1,
  };
  const n = sizes[def] ?? 2;
  const c = newEntityCanvas(n, 26, 10);
  const ruined = state === 'ruin';
  const r = ruined ? ruinRamp(depth.structureBase) : rampFor(depth);
  const sig = sigRamp(SIGNATURE_ECONOMY);
  const lamp = state === 'working' ? sig.on : state === 'stalled' ? sig.dim : null;

  if (ruined) {
    isoPrism(c, { n, inset: Math.max(1, 8 * n - 6), height: 3, ramp: r });
    c.s.fillRect(c.ax - 5, c.ay - 8, 2, 4, r.left, 'set');
    c.s.fillRect(c.ax + 3, c.ay - 7, 2, 3, r.right, 'set');
    applyOutline(c.s);
    return { ground: placeholderGround(depth, n), body: toSprite(c, n) };
  }

  switch (def) {
    case 'smelter': {
      // box + chimney, furnace mouth glowing when fed
      const top = isoPrism(c, { n, inset: 5, height: 12, ramp: r });
      isoCylinder(c, { rx: 5, height: 9, lift: c.ay - top, dx: -8, ramp: r });
      if (lamp) c.s.fillRect(c.ax + 4, c.ay - 6, 4, 2, state === 'working' ? sig.hot : lamp, 'set');
      break;
    }
    case 'bullet_kitchen': {
      // squat box with three round "burners" on the roof
      const top = isoPrism(c, { n, inset: 5, height: 10, ramp: r });
      for (const dx of [-8, 0, 8]) {
        isoCylinder(c, { rx: 3, height: 2, lift: c.ay - top, dx, ramp: r });
        if (lamp) c.s.px(c.ax + dx, top - 4, lamp);
      }
      break;
    }
    case 'gas_tap': {
      // low dome over the vent + valve mast
      isoCylinder(c, { rx: 12, height: 6, ramp: r });
      isoCylinder(c, { rx: 3, height: 12, lift: 6, ramp: r });
      if (lamp) c.s.fillRect(c.ax - 1, c.ay - 20, 2, 2, lamp, 'set');
      break;
    }
    case 'refinery': {
      // the paired-cylinders family
      isoCylinder(c, { rx: 8, height: 16, dx: -9, dy: -2, ramp: r });
      isoCylinder(c, { rx: 8, height: 12, dx: 9, dy: 2, ramp: r });
      c.s.hline(c.ax - 4, c.ax + 4, c.ay - 13, r.edge, 'set'); // transfer pipe
      if (lamp) c.s.px(c.ax - 9, c.ay - 19, lamp);
      break;
    }
    case 'generator': {
      // squat barrel + exhaust stack; runs hot
      isoCylinder(c, { rx: 13, height: 10, ramp: r });
      isoCylinder(c, { rx: 3, height: 8, lift: 10, dx: 8, dy: 2, ramp: r });
      if (lamp) {
        c.s.fillRect(c.ax - 2, c.ay - 12, 4, 2, state === 'working' ? sig.hot : lamp, 'set');
      }
      break;
    }
    case 'storage_depot': {
      // wide low slab with crate bumps
      const top = isoPrism(c, { n, inset: 3, height: 6, ramp: r });
      isoPrism(c, { n: 1, inset: 2, height: 4, lift: c.ay - top, dx: -7, dy: -3, ramp: r });
      isoPrism(c, { n: 1, inset: 3, height: 3, lift: c.ay - top, dx: 8, dy: 3, ramp: r });
      if (lamp) c.s.px(c.ax + 1, c.ay - 8, lamp);
      break;
    }
    case 'power_pylon': {
      // ringed mast, cyan tip — energy is cyan (doc 10 rule 6)
      isoPrism(c, { n: 1, inset: 5, height: 2, ramp: r });
      c.s.fillRect(c.ax - 1, c.ay - 20, 2, 18, r.left, 'set');
      for (const dy of [7, 12]) c.s.hline(c.ax - 2, c.ax + 1, c.ay - dy - 2, r.edge, 'set');
      const tip = state === 'working' ? depth.playerGlow : shade(depth.playerGlow, 0.4);
      if (state !== 'offgrid') c.s.fillRect(c.ax - 1, c.ay - 22, 2, 2, tip, 'set');
      break;
    }
    default:
      isoPrism(c, { n, inset: 4, height: 10, ramp: r });
  }

  if (state === 'offgrid') {
    // 50% checkerboard dim — the doc 10 brownout/disabled treatment
    for (let y = 0; y < c.s.height; y++) {
      for (let x = (y & 1); x < c.s.width; x += 2) {
        if (c.s.alphaAt(x, y) > 0) c.s.px(x, y, rampFor(depth).deep, 'set');
      }
    }
  }
  applyOutline(c.s);
  return { ground: placeholderGround(depth, n), body: toSprite(c, n) };
}

function placeholderGround(depth: DepthPalette, n: number): Sprite {
  const g = groundRamp(depth);
  const pad = 8;
  const s = new PixelSurface(TW * n * 2 + pad * 2, TH * n * 2 + pad * 2);
  const ax = TW * n + pad;
  const ay = TH * n + pad;
  isoDisc(s, ax, ay + 1, TW * n - 2, g.contactShadow);
  isoDiamondRim(s, ax, ay, TH * n, g.padRim);
  return { surface: s.resetOps(), ax, ay, n, ops: 0 };
}
